import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiCheck, mdiPlus, mdiTagPlusOutline } from '@mdi/js'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command'
import { useTags } from '../../api/tags'
import type { Tag } from '../../api/tags'
import { useDebounce } from '../../hooks/useDebounce'

interface TagPickerProps {
    orgCode: string
    entityType?: string
    selectedTags: Tag[]
    onTagAdd: (tag: Tag) => void
    onTagRemove: (tag: Tag) => void
    onTagCreate?: (name: string) => void
    maxTags?: number
    disabled?: boolean
}

export function TagPicker({ orgCode, entityType, selectedTags, onTagAdd, onTagRemove, onTagCreate, maxTags = 10, disabled }: TagPickerProps) {
    const { t } = useTranslation('common')
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search, 300)

    const { data: tags = [] } = useTags(orgCode, {
        q: debouncedSearch || undefined,
        target_entity_type: entityType,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedUids = new Set(selectedTags.map(t => t.uid))
    const isAtLimit = selectedTags.length >= maxTags

    const handleSelect = useCallback((tag: Tag) => {
        if (selectedUids.has(tag.uid!)) {
            onTagRemove(tag)
        } else if (!isAtLimit) {
            onTagAdd(tag)
        }
    }, [selectedUids, isAtLimit, onTagAdd, onTagRemove])

    const hasExactMatch = tags.some(t => t.name?.toLowerCase() === search.toLowerCase())

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="tag-picker"
                    aria-label={t('tags.add_tag')}
                    title={t('tags.add_tag')}
                    disabled={disabled}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <AppIcon path={mdiTagPlusOutline} size="xs" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput
                        data-testid="tag-picker-search"
                        placeholder={t('tags.search')}
                        value={search}
                        onValueChange={setSearch}
                    />
                    <CommandList>
                        <CommandEmpty>{t('tags.no_tags')}</CommandEmpty>
                        <CommandGroup>
                            {tags.map((tag) => (
                                <CommandItem
                                    key={tag.uid}
                                    value={tag.uid}
                                    onSelect={() => handleSelect(tag)}
                                    disabled={isAtLimit && !selectedUids.has(tag.uid!)}
                                >
                                    <div className="flex items-center gap-2 w-full">
                                        {tag.color && (
                                            <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                                        )}
                                        <span className="flex-1 truncate">{tag.name}</span>
                                        {selectedUids.has(tag.uid!) && <AppIcon path={mdiCheck} size="sm" className="shrink-0" />}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                        {onTagCreate && search && !hasExactMatch && (
                            <CommandGroup>
                                <CommandItem
                                    data-testid="tag-picker-create"
                                    onSelect={() => {
                                        onTagCreate(search)
                                        setSearch('')
                                    }}
                                >
                                    <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                    {t('tags.create_new')}: &quot;{search}&quot;
                                </CommandItem>
                            </CommandGroup>
                        )}
                        {isAtLimit && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                                {t('tags.max_tags_reached')}
                            </div>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
